import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import Cookies from "js-cookie";
import BillingPageFrame from "./BillingPageFrame";
import { listTransactions } from "./billingApi";
import {
    CATEGORY_LABELS,
    formatCreditCount,
    formatRolesForDisplay,
    formatRupeesFromPaisa,
    formatWhen,
    ledgerServicesForRoles,
    parseRoleList,
    serviceLabel,
} from "./billingFormat";
import { useBilling } from "../../context/BillingContext";

const REFERENCE_LABELS = {
    email_schedule: "Email schedule",
    whatsapp_messages: "WhatsApp message",
};

const dayLabel = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Unknown date";
    const start = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const diff = Math.round((start(new Date()) - start(date)) / 86400000);
    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const personLabel = (row, walletUserId) => {
    const name = row.user_name || row.name || row.user?.name;
    if (name) return name;
    if (row.email) return row.email;
    if (row.user_id != null && walletUserId != null && String(row.user_id) === String(walletUserId)) return "You";
    return "This account";
};

const rowHaystack = (row, walletUserId) => [
    personLabel(row, walletUserId),
    row.name,
    row.email,
    serviceLabel(row.service, row.template_category),
    row.service,
    row.reference_type,
    row.reference_id,
    row.transaction_type,
].filter((part) => part != null && part !== "").join(" ").toLowerCase();

const DetailLine = ({ label, children }) => (
    <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{label}</Typography>
        <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>{children}</Typography>
    </Box>
);

const CreditLedgerPage = () => {
    const { isCreditBased, wallet, loading: billingLoading } = useBilling();
    const serviceOptions = useMemo(
        () => ledgerServicesForRoles(parseRoleList(Cookies.get("userRole"))),
        []
    );
    const [service, setService] = useState("");
    const [transactionType, setTransactionType] = useState("");
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [rows, setRows] = useState([]);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [copiedKey, setCopiedKey] = useState("");

    const load = useCallback(async () => {
        if (!isCreditBased) return;
        setLoading(true);
        setError("");
        try {
            const params = { page, per_page: perPage };
            if (service) params.service = service;
            if (transactionType) params.transaction_type = transactionType;
            const result = await listTransactions(params);
            setRows(result.rows);
            setLastPage(result.lastPage);
            setTotal(result.total);
        } catch (err) {
            setRows([]);
            setTotal(0);
            setError(err?.status === 403
                ? "Only the account admin can view the ledger."
                : (err?.message || "Could not load the ledger"));
        } finally {
            setLoading(false);
        }
    }, [isCreditBased, page, perPage, service, transactionType]);

    useEffect(() => {
        if (billingLoading) return;
        if (!isCreditBased) {
            setLoading(false);
            return;
        }
        load();
    }, [billingLoading, isCreditBased, load]);

    useEffect(() => {
        setExpandedId(null);
    }, [page, service, transactionType, query]);

    useEffect(() => {
        if (service && !serviceOptions.includes(service)) {
            setService("");
            setPage(1);
        }
    }, [service, serviceOptions]);

    const visibleRows = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter((row) => rowHaystack(row, wallet?.user_id).includes(needle));
    }, [query, rows, wallet?.user_id]);

    const totals = useMemo(() => {
        return visibleRows.reduce((sum, row) => {
            const amount = Number(row.credit_spent) || 0;
            if (row.transaction_type === "CREDIT") sum.in += amount;
            else sum.out += amount;
            return sum;
        }, { in: 0, out: 0 });
    }, [visibleRows]);

    const copyText = async (key, text) => {
        try {
            await navigator.clipboard.writeText(String(text));
            setCopiedKey(key);
            window.setTimeout(() => setCopiedKey((current) => (current === key ? "" : current)), 1500);
        } catch {
            setCopiedKey("");
        }
    };

    const grouped = useMemo(() => {
        const order = [];
        const map = new Map();
        visibleRows.forEach((row, index) => {
            const label = dayLabel(row.created_at);
            if (!map.has(label)) {
                map.set(label, []);
                order.push(label);
            }
            map.get(label).push({ row, index });
        });
        return order.map((label) => ({ label, items: map.get(label) }));
    }, [visibleRows]);

    const filtersActive = Boolean(service || transactionType || query);

    return (
        <BillingPageFrame
            title="Ledger"
            subtitle="Credits added and spent on this account, including sub-users. 1 credit = 1 paisa. Open a row for the full entry."
            maxWidth={1180}
        >
            {!billingLoading && !isCreditBased ? (
                <Alert severity="info">Ledger is available on credit accounts. Your balance is under Credits Information.</Alert>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
                        {[
                            { label: "On this view", value: String(visibleRows.length), hint: total ? `${total} matching entries` : "Loaded entries" },
                            { label: "Credit in", value: formatRupeesFromPaisa(totals.in), hint: formatCreditCount(totals.in), color: "#047857" },
                            { label: "Credit out", value: formatRupeesFromPaisa(totals.out), hint: formatCreditCount(totals.out), color: "#b91c1c" },
                        ].map((card) => (
                            <Box key={card.label} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, px: 2, py: 1.5, bgcolor: "#fff" }}>
                                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: card.color || "#111827", letterSpacing: "-0.02em" }}>
                                    {card.value}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{card.hint}</Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.25 }}>
                            {[
                                { value: "", label: "All" },
                                { value: "CREDIT", label: "Credit in" },
                                { value: "DEBIT", label: "Credit out" },
                            ].map((option) => {
                                const selected = transactionType === option.value;
                                return (
                                    <Chip
                                        key={option.label}
                                        label={option.label}
                                        clickable
                                        color={selected ? "primary" : "default"}
                                        variant={selected ? "filled" : "outlined"}
                                        onClick={() => { setTransactionType(option.value); setPage(1); }}
                                    />
                                );
                            })}
                        </Box>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
                            <Chip
                                label="All services"
                                size="small"
                                clickable
                                color={service === "" ? "primary" : "default"}
                                variant={service === "" ? "filled" : "outlined"}
                                onClick={() => { setService(""); setPage(1); }}
                            />
                            {serviceOptions.map((key) => (
                                <Chip
                                    key={key}
                                    label={serviceLabel(key)}
                                    size="small"
                                    clickable
                                    color={service === key ? "primary" : "default"}
                                    variant={service === key ? "filled" : "outlined"}
                                    onClick={() => { setService(service === key ? "" : key); setPage(1); }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <TextField
                                size="small"
                                placeholder="Search name, email, or reference"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                sx={{ minWidth: 240, flex: 1 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <TextField
                                select
                                size="small"
                                label="Per page"
                                value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                sx={{ width: 110 }}
                            >
                                {[10, 20, 50].map((size) => (
                                    <MenuItem key={size} value={size}>{size}</MenuItem>
                                ))}
                            </TextField>
                            <Tooltip title="Reload ledger">
                                <span>
                                    <IconButton onClick={load} disabled={loading} aria-label="Reload ledger">
                                        <RefreshIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {filtersActive && (
                                <Button
                                    size="small"
                                    onClick={() => { setService(""); setTransactionType(""); setQuery(""); setPage(1); }}
                                >
                                    Clear
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {error && (
                        <Alert
                            severity="error"
                            action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
                        >
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ position: "relative", border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff", overflow: "hidden" }}>
                        {loading && <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0 }} />}
                        {loading && rows.length === 0 ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress size={28} /></Box>
                        ) : visibleRows.length === 0 ? (
                            <Box sx={{ px: 2, py: 5, textAlign: "center" }}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No ledger entries</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {filtersActive
                                        ? "Nothing on this page matches the search or filters."
                                        : "Credits you add or spend will show up here."}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                            {["When", "Who", "Activity", "Amount", ""].map((heading) => (
                                                <TableCell key={heading || "expand"} sx={{ fontWeight: 700, color: "#475569" }}>{heading}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {grouped.map((group) => (
                                            <React.Fragment key={group.label}>
                                                <TableRow>
                                                    <TableCell colSpan={5} sx={{ py: 1, bgcolor: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: "0.04em", color: "#64748b" }}>
                                                            {group.label}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                {group.items.map(({ row, index }) => {
                                                    const rowKey = row.id ?? `${page}-${index}`;
                                                    const open = expandedId === rowKey;
                                                    const credit = row.transaction_type === "CREDIT";
                                                    const roles = formatRolesForDisplay(row.user_role || row.user?.user_role);
                                                    const reference = row.reference_type
                                                        ? `${REFERENCE_LABELS[row.reference_type] || row.reference_type} #${row.reference_id ?? "—"}`
                                                        : null;
                                                    return (
                                                        <React.Fragment key={rowKey}>
                                                            <TableRow
                                                                hover
                                                                onClick={() => setExpandedId(open ? null : rowKey)}
                                                                sx={{ cursor: "pointer", bgcolor: open ? "#f8fafc" : "#fff" }}
                                                            >
                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>{formatWhen(row.created_at)}</TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        {personLabel(row, wallet?.user_id)}
                                                                    </Typography>
                                                                    {row.email && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                            {row.email}
                                                                        </Typography>
                                                                    )}
                                                                    {roles && (
                                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                                            {roles}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                        {serviceLabel(row.service, row.template_category)}
                                                                    </Typography>
                                                                    <Chip
                                                                        size="small"
                                                                        label={credit ? "Credit in" : "Credit out"}
                                                                        sx={{
                                                                            mt: 0.5,
                                                                            height: 22,
                                                                            fontWeight: 700,
                                                                            bgcolor: credit ? "#ecfdf5" : "#fef2f2",
                                                                            color: credit ? "#047857" : "#b91c1c",
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                    <Typography sx={{ fontWeight: 800, color: credit ? "#047857" : "#b91c1c" }}>
                                                                        {credit ? "+" : "−"}{formatRupeesFromPaisa(row.credit_spent)}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {formatCreditCount(row.credit_spent)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="right" sx={{ width: 48 }}>
                                                                    <ExpandMoreIcon
                                                                        fontSize="small"
                                                                        sx={{
                                                                            color: "#64748b",
                                                                            transform: open ? "rotate(180deg)" : "none",
                                                                            transition: "transform 160ms ease",
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
                                                                    <Collapse in={open} timeout="auto" unmountOnExit>
                                                                        <Box
                                                                            sx={{
                                                                                display: "grid",
                                                                                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                                                                                gap: 2,
                                                                                px: 1,
                                                                                py: 2,
                                                                            }}
                                                                            onClick={(event) => event.stopPropagation()}
                                                                        >
                                                                            <DetailLine label="Transaction">{row.id ?? "—"}</DetailLine>
                                                                            <DetailLine label="Name">{row.name || personLabel(row, wallet?.user_id)}</DetailLine>
                                                                            <DetailLine label="Email">{row.email || "—"}</DetailLine>
                                                                            <DetailLine label="Service">{row.service ? serviceLabel(row.service) : "—"}</DetailLine>
                                                                            <DetailLine label="Category">{CATEGORY_LABELS[row.template_category] || row.template_category || "—"}</DetailLine>
                                                                            <DetailLine label="Reference">
                                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                                                    {reference || "—"}
                                                                                    {row.reference_id != null && (
                                                                                        <Tooltip title={copiedKey === `ref-${rowKey}` ? "Copied" : "Copy reference"}>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                aria-label="Copy reference"
                                                                                                onClick={() => copyText(`ref-${rowKey}`, row.reference_id)}
                                                                                            >
                                                                                                {copiedKey === `ref-${rowKey}`
                                                                                                    ? <CheckIcon sx={{ fontSize: 16, color: "#047857" }} />
                                                                                                    : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                                                                                            </IconButton>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </Box>
                                                                            </DetailLine>
                                                                            {row.service === "EMAIL-AGENT" && (
                                                                                <DetailLine label="Tokens">
                                                                                    {`${row.prompt_tokens || 0} in / ${row.output_tokens || 0} out`}
                                                                                </DetailLine>
                                                                            )}
                                                                            {row.characters != null && (
                                                                                <DetailLine label="Characters">{row.characters}</DetailLine>
                                                                            )}
                                                                        </Box>
                                                                    </Collapse>
                                                                </TableCell>
                                                            </TableRow>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                        <Typography variant="body2" color="text.secondary">
                            {query
                                ? `${visibleRows.length} of ${rows.length} on this page match the search`
                                : `Page ${page} of ${lastPage}`}
                        </Typography>
                        {lastPage > 1 && (
                            <Pagination
                                count={lastPage}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                                shape="rounded"
                            />
                        )}
                    </Box>
                </Box>
            )}
        </BillingPageFrame>
    );
};

export default CreditLedgerPage;
